<template>
  <div class="flex relative md:pt-12 mx-auto px-4 overflow-hidden w-full max-w-7xl">
    <div class="flex-1">
      <div class="relative">
      <div class="flex items-center float-right">
        <span v-if="enablePrintDocuments && program.id" class="link text-underline flex items-center" @click="() => downloadDocument(program.id)">
          <BaseIcon
            v-if="isDownloadingDocument"
            icon="spinner"
            class="spinner inline-block mr-2"
          />
          <h2>Download as PDF</h2>
        </span>
      </div>
        <div class="lg:mx-auto lg:max-w-7xl flex flex-col lg:flex-row">
          <div class="px-4 max-w-xl mx-auto sm:px-6 lg:py-8 lg:max-w-3xl lg:mx-0 lg:px-0">
            <div>
              <div class="theme-page-content mt-6 mr-4">
                <div class="flex items-center">
                  <h1 class="pb-2 mb-0">{{ programDisplayHeading }}</h1>
                  <RevisionSelector
                    v-if="program.programGroupId"
                    class="ml-2 pb-5 self-center"
                    entity-type="programs"
                    :entity-id="program.programGroupId"
                  />
                </div>
                <p
                  v-if="program.catalogDescription || program.description"
                  class="mt-4 text-lg text-gray-500"
                >
                  {{ program.catalogDescription || program.description }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="quote"
        class="py-12 bg-gray-50 overflow-hidden md:py-20 lg:py-24"
      >
        <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="relative">
            <blockquote class="mt-10">
              <div class="max-w-3xl mx-auto text-center text-2xl leading-9 font-medium text-gray-900">
                <p>
                  &ldquo;{{ quote.text }}&rdquo;
                </p>
              </div>
              <footer
                v-if="quote.author"
                class="mt-8"
              >
                <div class="md:flex md:items-center md:justify-center">
                  <div class="mt-4 text-center md:mt-0 md:ml-4 md:flex md:items-center">
                    <div class="text-base font-medium text-gray-900">{{ quote.author }}</div>
                    <svg
                      v-if="quote.authorDescription"
                      class="hidden md:block mx-1 h-5 w-5 text-indigo-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M11 0h3L9 20H6l5-20z" />
                    </svg>
                    <div class="text-base font-medium text-gray-500">{{ quote.authorDescription }}</div>
                  </div>
                </div>
              </footer>
            </blockquote>
          </div>
        </div>
      </div>
      <div
        v-if="program.catalogFullDescription"
        class="relative py-16 overflow-hidden mt-8"
      >
        <div class="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div class="text-lg ">
            <h1>
              <span class="mt-2 block text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">About The Program</span>
            </h1>
          </div>
          <RichContent
            class="mt-12 content-container theme-page-content"
            :content="program.catalogFullDescription"
          />
        </div>
      </div>
      <div
        v-if="careerPaths.length"
        class="py-12 mt-8 bg-gray-50"
      >
        <div class="max-w-7xl mx-auto">
          <h1 class="py-4">
            <span class="mt-2 block text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">Careers</span>
          </h1>
          <div
            v-for="(career, index) in careerPaths"
            :key="'career-' + index"
            class="mt-4 border-t border-gray-200 mx-4"
          >
            <div class="divide-y divide-gray-200">
              <div class="sm:py-5 flex flex-col lg:gap-2">
                <div class="text-sm text-gray-700">
                  <span class="font-semibold text-lg text-gray-900">{{ career.name }}</span>
                </div>
                <div class="text-sm flex-grow">
                  <span>{{ career.description }}</span>
                </div>
                <div class="text-sm">
                  Employeers:&nbsp;
                  <span class="flex-grow">{{ career.employers }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="lg:mx-auto lg:max-w-7xl">
        <PageTemplateRenderer
          :content="program"
          type="program"
        />
      </div>
      <div
        v-if="program.contacts && program.contacts.length"
        class="bg-white my-4"
      >
        <div class="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <h1 class="pb-8">
            <span class="mt-2 block text-3xl text-center leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">Get In Touch</span>
          </h1>
          <div class="max-w-lg mx-auto md:max-w-none flex flex-col lg:flex-row justify-center py-4">
            <div
              v-for="(contact, index) in program.contacts"
              :key="`contacts-${index}`"
              class="mx-auto w-96"
              :class="index && 'mt-12 md:mt-16 lg:mt-0'"
            >
              <h2
                v-if="contact.title"
                class="text-xl font-bold text-gray-900"
              >
                {{ contact.title }}
              </h2>
              <div
                v-if="contact.name"
                class="mt-4"
              >
                <p class="text-lg text-gray-700">
                  {{ contact.name }}
                </p>
              </div>
              <div class="mt-2">
                <div
                  v-if="contact.phone"
                  class="flex"
                >
                  <div class="flex-shrink-0">
                    <svg
                      class="h-6 w-6 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div class="ml-4 text-base text-gray-700">
                    <p>
                      {{ contact.phone }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="contact.email"
                  class="mt-2 flex"
                >
                  <div class="flex-shrink-0">
                    <svg
                      class="h-6 w-6 text-gray-400"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div class="ml-4 text-base text-gray-700">
                    <a
                      href="#"
                      class="font-medium text-gray-700 underline"
                    >
                      {{ contact.email }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <slot name="sidebar" />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RichContent from '@/components/RichContent'
import PageTemplateRenderer from '@/components/PageTemplateRenderer'
import { generateProgramLabel } from '@/utils/dataLabels'
import useInterpolation from '@/composables/useInterpolation'
import useFeatureFlags from '@/composables/useFeatureFlags'
import useDocumentsDownload from '@/composables/useDocumentsDownload'
import { TITLE_INTERPOLATIONS, PRINT_DOCUMENTS } from '@/constants/featureFlags'
import RevisionSelector from '@/components/RevisionSelector'

export default {
  components: { RichContent, PageTemplateRenderer, RevisionSelector },
  props: {
    program: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { enableTitleInterpolations, enablePrintDocuments } = useFeatureFlags(
      {
        enableTitleInterpolations: TITLE_INTERPOLATIONS,
        enablePrintDocuments: PRINT_DOCUMENTS,
      }
    )
    const { getInterpolatedFieldValue } = useInterpolation()
    const { downloadDocument, isDownloadingDocument } = useDocumentsDownload(
      'program'
    )
    return {
      getInterpolatedFieldValue,
      enableTitleInterpolations,
      enablePrintDocuments,
      downloadDocument,
      isDownloadingDocument,
    }
  },
  computed: {
    ...mapState('settings', ['school']),
    quote() {
      if (!this.program.catalogQuote) return null
      const author = this.program.catalogQuoteAuthor || ''
      return {
        text: this.program.catalogQuote,
        author: author.split('/')[0],
        authorDescription: author.split('/')[1],
      }
    },
    careerPaths() {
      if (!this.program.careerPaths) return []
      return this.program.careerPaths.filter((el) => el.showInCatalog)
    },
    programDisplayHeading() {
      return generateProgramLabel(this.program, this.school, {
        getInterpolatedFieldValue: this.enableTitleInterpolations
          ? this.getInterpolatedFieldValue
          : undefined,
        nameOnlyFallback: true,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.sidebar-container {
  @media (min-width: 768px) {
    width: 300px;
  }
}
.link {
  cursor: pointer;
  text-decoration: underline;
}
</style>
